import {FormControl, FormHelperText, InputLabel} from '@mui/material';
import {useField, useFormikContext} from 'formik';
import {isEmpty, notEmpty} from 'main/utility/CommonUtil';
import {
  getFieldLabel,
  getFormFieldLabel,
  getOptFldValidInitValues,
  isMultiValueField,
} from 'main/utility/LayoutUtil';
import PropTypes from 'prop-types';
import {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getWidgetFieldOptions} from 'redux/actions';
import {UiWiseFormFieldOptionControl} from 'shared/constants/UiWiseConst';
import AppOptFldCheckbox from './AppOptFldCheckbox';
import AppOptFldRadio from './AppOptFldRadio';
import AppOptFldSelectList from './AppOptFldSelectList';
import AppOptFldSlider from './AppOptFldSlider';
import AppOptFldSwitch from './AppOptFldSwitch';
import AppOptFldToggle from './AppOptFldToggle';

const getOptionControl = (props, fieldOptions) => {
  const commonProps = {
    ...props,
    options: fieldOptions,
  };

  switch (props.optioncontrol) {
    case UiWiseFormFieldOptionControl.SELECT_LIST:
      return <AppOptFldSelectList {...commonProps} />;

    case UiWiseFormFieldOptionControl.SWITCH:
      return <AppOptFldSwitch {...commonProps} />;

    case UiWiseFormFieldOptionControl.CHECKBOX:
      return <AppOptFldCheckbox {...commonProps} />;

    case UiWiseFormFieldOptionControl.RADIO:
      return <AppOptFldRadio {...commonProps} />;

    case UiWiseFormFieldOptionControl.TOGGLE_BUTTON:
      return <AppOptFldToggle {...commonProps} />;

    case UiWiseFormFieldOptionControl.SLIDER:
      return <AppOptFldSlider {...commonProps} />;

    default:
      return null;
  }
};

const AppOptionField = ({multivalue, ...props}) => {
  const {
    name,
    optioncontrol,
    widgetid,
    min,
    max,
    disabled,
    parentfieldname,
    options: specoptions,
  } = props;

  const dispatch = useDispatch();
  const [field, meta] = useField(props);
  const {setFieldValue, values} = useFormikContext();

  const fieldOptions = useSelector(
    ({widgetReducer}) =>
      widgetReducer.widgetFieldOptionsById?.[widgetid]?.[name],
  );

  const optionList = specoptions ?? fieldOptions ?? [];
  const errorText = meta.error && meta.touched ? meta.error : '';
  const isMulti = isMultiValueField({...props, multivalue});

  const isSelect = optioncontrol === UiWiseFormFieldOptionControl.SELECT_LIST;
  const isSlider = optioncontrol === UiWiseFormFieldOptionControl.SLIDER;
  const hasRange = isSlider && min < max;
  const needsFetch = isEmpty(specoptions) && !hasRange;

  const prevParentValueRef = useRef();
  const parentValue = parentfieldname ? values[parentfieldname] : null;

  useEffect(() => {
    const hasParentChanged = Boolean(
      parentfieldname &&
        notEmpty(parentValue) &&
        prevParentValueRef.current !== parentValue,
    );

    // Fetch if:
    //    - It's a standard field and it's empty
    //    - OR it's a child field and the parent just changed
    //    - OR it's a child field and it's the very first load (prev is undefined)
    const shouldFetch =
      (needsFetch && isEmpty(fieldOptions)) || hasParentChanged;

    if (shouldFetch) {
      const params = parentfieldname ? {[parentfieldname]: parentValue} : {};
      dispatch(getWidgetFieldOptions(widgetid, name, params));
      prevParentValueRef.current = parentValue;
    }

    // Reset child value if parent becomes empty
    if (parentfieldname && !parentValue && notEmpty(field.value)) {
      setFieldValue(name, isMulti ? [] : '');
      prevParentValueRef.current = null;
    }
  }, [
    dispatch,
    widgetid,
    name,
    needsFetch,
    fieldOptions,
    parentfieldname,
    parentValue,
  ]);

  useEffect(() => {
    if (isSlider || !needsFetch) return;

    const selectedValue = getOptFldValidInitValues(
      field.value,
      optionList,
      isMulti,
    );

    const hasChanged = isMulti
      ? JSON.stringify(selectedValue) !== JSON.stringify(field.value)
      : selectedValue !== field.value;

    // Only update if there is a change
    if (hasChanged) {
      setFieldValue(field.name, selectedValue);
    }
  }, [optionList, isSlider, isMulti, name]);

  return (
    <FormControl error={!!errorText} fullWidth disabled={disabled}>
      {isSelect ? (
        <InputLabel id={props.labelid}>
          {getFieldLabel(props.label, props.required)}
        </InputLabel>
      ) : (
        getFormFieldLabel(props)
      )}

      {getOptionControl({...props, multivalue: isMulti}, optionList)}

      {errorText && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};

AppOptionField.propTypes = {
  name: PropTypes.string.isRequired,
  widgetid: PropTypes.string.isRequired,

  /** Labeling */
  label: PropTypes.string,
  labelid: PropTypes.string,
  required: PropTypes.bool,

  /** Behavior */
  disabled: PropTypes.bool,
  multivalue: PropTypes.bool,
  optionalignment: PropTypes.oneOf(['horizontal', 'vertical']),
  parentfieldname: PropTypes.string,

  /** Option control type and Options */
  optioncontrol: PropTypes.oneOf(Object.values(UiWiseFormFieldOptionControl))
    .isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.any.isRequired,
    }),
  ),

  /** Slider-specific */
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
};

export default AppOptionField;
